import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Selector } from 'sonata-react-components';

import { ViewToggle } from '@/components/ViewToggle/ViewToggle';
import HeatmapTagBar from '../HeatmapTagBar/HeatmapTagBar';

import type { SelectorOptionPropsInterface } from 'sonata-react-components';
import type { MetricFilter, TagItem } from '@/types/footfall';

import './visitationMetricTrendsHeader.scss';

type VisitsMetric = 'uplift' | 'baseline' | 'cpsv';

const METRIC_FILTER_OPTIONS: SelectorOptionPropsInterface[] = [
  { id: 'uplift', label: 'Uplift', value: 'uplift' },
  { id: 'baseline', label: 'Baseline', value: 'baseline' },
  { id: 'cpsv', label: 'CPSV', value: 'cpsv' }
];

type Props = {
  tags: TagItem[];
  activeTagIds: string[];
  onTagClick: (tagId: string) => void;
  onAllTagsClick: () => void;
  metricFilter: MetricFilter;
  onMetricFilterChange: (filter: MetricFilter) => void;
};

type State = {
  visitsMetric: VisitsMetric;
};

export default function VisitationMetricTrendsHeader({
  tags,
  activeTagIds,
  onTagClick,
  onAllTagsClick,
  metricFilter,
  onMetricFilterChange
}: Readonly<Props>) {
  const { formatMessage } = useIntl();

  const [{ visitsMetric }, setState] = useState<State>({
    visitsMetric: metricFilter !== 'percentage' ? (metricFilter as VisitsMetric) : 'uplift'
  });

  const toggleValue: 'percentage' | 'uplift' = metricFilter === 'percentage' ? 'percentage' : 'uplift';

  const handleToggleChange = (value: 'percentage' | 'uplift') => {
    onMetricFilterChange(value === 'percentage' ? 'percentage' : visitsMetric);
  };

  const handleSelectorChange = (metric: VisitsMetric) => {
    setState({ visitsMetric: metric });
    onMetricFilterChange(metric === 'uplift' ? 'percentage' : metric);
  };

  return (
    <div className="visitationMetricTrendsHeader">
      {/* Row 1: title + ranking label */}
      <div className="visitationMetricTrendsHeader__titleRow">
        <div className="visitationMetricTrendsHeader__left">
          <i className="fa-solid fa-table visitationMetricTrendsHeader__icon" />
          <h2 className="visitationMetricTrendsHeader__title">
            {formatMessage({
              defaultMessage: 'Store Footfall & Visitation Metric Trends',
              id: 'aggregatedReport.visitationMetricTrends.title'
            })}
          </h2>
        </div>
        <span className="visitationMetricTrendsHeader__rankingLabel">
          {formatMessage({
            defaultMessage: 'Ranking of ',
            id: 'aggregatedReport.visitationMetricTrends.rankingLabelPrefix'
          })}
          <span className="visitationMetricTrendsHeader__rankingLabelHighlight">
            {formatMessage({
              defaultMessage: 'Top Performing Stores',
              id: 'aggregatedReport.visitationMetricTrends.rankingLabelHighlight'
            })}
          </span>
          {formatMessage({
            defaultMessage: ' by Daily Traffic',
            id: 'aggregatedReport.visitationMetricTrends.rankingLabelSuffix'
          })}
        </span>
      </div>

      {/* Row 2: tags (left) + ViewToggle + filter (right) */}
      <div className="visitationMetricTrendsHeader__controlsRow">
        <div className="visitationMetricTrendsHeader__tagsSlot">
          <HeatmapTagBar
            activeTagIds={activeTagIds}
            onAllTagsClick={onAllTagsClick}
            onTagClick={onTagClick}
            tags={tags}
          />
        </div>
        <div className="visitationMetricTrendsHeader__rightButtons">
          {visitsMetric === 'uplift' && (
            <ViewToggle
              onChange={handleToggleChange}
              options={[
                {
                  icon: 'fa-regular fa-percentage',
                  label: formatMessage({
                    defaultMessage: 'Percentage',
                    id: 'aggregatedReport.visitationMetricTrends.percentage'
                  }),
                  value: 'percentage'
                },
                {
                  icon: 'fa-solid fa-users-viewfinder',
                  label: formatMessage({
                    defaultMessage: 'Uplift visits',
                    id: 'aggregatedReport.visitationMetricTrends.upliftVisits'
                  }),
                  value: 'uplift'
                }
              ]}
              value={toggleValue}
              variant="interaction"
            />
          )}
          <div className="visitationMetricTrendsHeader__selectorContainer">
            <span className="visitationMetricTrendsHeader__selectorLabel">
              {formatMessage({
                defaultMessage: 'Filtered by:',
                id: 'aggregatedReport.visitationMetricTrends.metricFilterLabel'
              })}
            </span>
            <Selector<SelectorOptionPropsInterface>
              className="visitationMetricTrendsHeader__selector"
              onChange={selected => {
                const value = (selected as SelectorOptionPropsInterface)?.value;
                console.log('Selector changed:', value);
                if (value) {
                  handleSelectorChange(value as VisitsMetric);
                }
              }}
              options={METRIC_FILTER_OPTIONS}
              tiny
              type="simple"
              value={METRIC_FILTER_OPTIONS.filter(opt => opt.value === visitsMetric)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
